import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { AddDiscountInput } from '$api/[shopId]/[cartId]/add-discount';
import type { Cart } from '@getjust/gateway';

import { CART_QUERY_KEY } from '$src/constants';
import { useBaseApiUrl } from '$src/hooks/client/useBaseApiUrl';
import { useCartIdUpdater } from '$src/hooks/client/useCartIdUpdater';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { gateway } from '$src/http';

const MUTATION_CART_ADD_DISCOUNT_KEY = 'MUTATION/ADD_DISCOUNT';

export const useAddDiscount = () => {
  const queryClient = useQueryClient();
  const baseUrl = useBaseApiUrl();
  const { track } = useTrackAnalytics();
  const updateCartId = useCartIdUpdater();

  const mutation = useMutation({
    mutationFn: (discount: AddDiscountInput) => gateway.post<Cart>(`${baseUrl}/add-discount`, discount),
    mutationKey: [MUTATION_CART_ADD_DISCOUNT_KEY, baseUrl],
    onSuccess: ({ data }, variables) => {
      updateCartId(data.id);
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, data.id], data);
      track(AnalyticsEventName.DISCOUNT_CODE_SUCCEEDED, { code: variables.code });
    },
    onError: (_, variables) => {
      track(AnalyticsEventName.DISCOUNT_CODE_FAILED, { code: variables.code });
    },
  });

  return {
    addDiscount: mutation.mutateAsync,
    status: mutation.status,
    reset: mutation.reset,
    // @ts-expect-error - exists
    error: mutation.error?.response?.data,
  };
};
